import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Helmet } from 'react-helmet';
import SubscriptionCheckout from '../../components/SubscriptionCheckout';
import FreeAccessNotice from '../../components/FreeAccessNotice';
import SiteMessageContainer from '../../components/SiteMessageContainer';

const AccountSetup = () => {
  const [step, setStep] = useState('setup'); // 'setup' or 'subscription'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountType, setAccountType] = useState('');
  const [displayName, setDisplayName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, updateProfile } = useAuth();

  // Get return path from state
  const returnTo = location.state?.returnTo || '/game-library-dashboard';

  useEffect(() => {
    // If no user, redirect to login
    if (!user) {
      navigate('/user-login');
      return;
    }

    // If user already has account setup completed, redirect to dashboard
    if (userProfile?.account_setup_completed) {
      navigate(returnTo);
      return;
    }

    // Pre-fill display name from user metadata or email
    if (user.user_metadata?.full_name) {
      setDisplayName(user.user_metadata.full_name);
    } else if (user.email) {
      setDisplayName(user.email.split('@')[0]);
    }
  }, [user, userProfile, navigate, returnTo]);

  const handleSetupComplete = async () => {
    if (!accountType) {
      setError('Please select an account type');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Update user profile with setup completion and preferences
      const updates = {
        full_name: displayName || user.email.split('@')[0],
        preferred_plan: accountType,
        account_setup_completed: true
      };

      const result = await updateProfile(updates);

      if (result.success) {
        if (accountType === 'free') {
          // For free accounts, go directly to dashboard
          navigate(returnTo);
        } else {
          // For premium accounts, move to subscription step
          setStep('subscription');
        }
      } else {
        setError(result.error || 'Failed to update profile. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Account setup error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscriptionSuccess = (subscriptionResult) => {
    console.log('Subscription created successfully:', subscriptionResult);
    navigate(returnTo);
  };

  const handleSubscriptionError = (errorMessage) => {
    setError(errorMessage);
  };

  const handleBackToSetup = () => {
    setStep('setup');
    setError('');
  };

  if (!user) {
    return null; // Will redirect in useEffect
  }

  return (
    <>
      <Helmet>
        <title>Complete Your Account Setup - Solis Games</title>
        <meta name="description" content="Complete your Solis Games account setup and choose your gaming plan." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
        {/* Background Gaming Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-secondary/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-accent/5 rounded-full blur-2xl animate-pulse"></div>
        </div>

        {step === 'setup' ? (
          // Account Setup Step
          <div className="relative w-full max-w-2xl">
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl shadow-gaming-xl p-8">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-primary mb-2">
                  Welcome to Solis Games! 🎮
                </h1>
                <p className="text-muted-foreground">
                  Let's complete your account setup to get you gaming
                </p>
              </div>

              {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-destructive">{error}</span>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Display Name */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="How should we call you?"
                  />
                </div>

                {/* Account Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-4">
                    Choose Your Gaming Experience
                  </label>

                  {/* Site Messages */}
                  <SiteMessageContainer page="account_setup" variant="full" className="mb-4" />

                  {/* Free Access Notice */}
                  <FreeAccessNotice variant="full" className="mb-4" />

                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Free Account */}
                    <div 
                      className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${
                        accountType === 'free' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setAccountType('free')}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold">Free Account</h3>
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          accountType === 'free' ? 'border-primary bg-primary' : 'border-border'
                        }`}>
                          {accountType === 'free' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Start gaming with limited access
                      </p>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                          Access to free games
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                          Basic features
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                          Limited game library
                        </li>
                      </ul>
                    </div>

                    {/* Premium Trial */}
                    <div 
                      className={`border-2 rounded-xl p-6 cursor-pointer transition-all relative ${
                        accountType === 'premium' 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setAccountType('premium')}
                    >
                      <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                        Popular
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold">Premium Trial</h3>
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          accountType === 'premium' ? 'border-primary bg-primary' : 'border-border'
                        }`}>
                          {accountType === 'premium' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">
                        Full access for just $5/month
                      </p>
                      <ul className="text-sm space-y-2">
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                          500+ premium games
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                          New games weekly
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                          All devices supported
                        </li>
                        <li className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                          Cancel anytime
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Continue Button */}
                <button
                  onClick={handleSetupComplete}
                  disabled={isLoading || !accountType}
                  className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  {isLoading ? 'Setting up...' : 'Continue'}
                </button>

                {/* Skip Option */}
                <div className="text-center">
                  <button
                    onClick={() => navigate(returnTo)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Skip for now (you can set this up later)
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Subscription Step
          <div className="relative w-full max-w-2xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary mb-2">
                Start Your Premium Gaming Experience
              </h1>
              <p className="text-muted-foreground">
                Complete your subscription to unlock all games
              </p>
            </div>

            <SubscriptionCheckout
              onSuccess={handleSubscriptionSuccess}
              onError={handleSubscriptionError}
              onCancel={handleBackToSetup}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default AccountSetup;
